import { useState } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  Users,
  Building2,
  AlertCircle,
  Loader2,
  Pencil,
  Trash2,
  Plus,
  X,
  Check,
  FolderTree,
} from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { PageChrome } from "@/components/tabs/PageChrome";
import { toast } from "sonner";
import { getLoginUrl } from "@/const";

/**
 * Admin page — manages the org's team hierarchy + members.
 *
 * Consumes the server's STRUCTURED tree (`admin.teamsTree` returns
 * `{ org, roots, flat }` with children pre-nested and dangling
 * parents already re-rooted server-side). The client renders it
 * verbatim — no flat→tree reassembly, no orphan promotion here.
 *
 * Every domain error is a stable `AdminError.code`; `errLabel`
 * is the ONE place those codes become localized copy.
 */

type TreeNode = {
  id: number;
  name: string;
  isContainer: boolean;
  memberCount: number;
  parentTeamId: number | null;
  children: TreeNode[];
};

/** Single source of truth: AdminError.code → human copy. */
function errLabel(code: string | undefined, locale: string): string {
  const ar = locale === "ar";
  const map: Record<string, [string, string]> = {
    forbidden: ["ليس لديك صلاحية", "You don't have permission"],
    team_not_found: ["الفريق غير موجود", "Team not found"],
    name_required: ["الاسم مطلوب", "Name is required"],
    team_has_members: [
      "أزل جميع الأعضاء أولاً",
      "Remove all members first",
    ],
    team_has_children: [
      "انقل أو احذف الفرق الفرعية أولاً",
      "Move or delete child teams first",
    ],
    team_has_activity: [
      "لا يمكن الحذف. للفريق تقارير أو أولويّات محفوظة. احذف بياناته أولاً.",
      "Can't delete. This team has saved reports or priorities. Clear its data first.",
    ],
    cannot_delete_personal: [
      "لا يمكن حذف مساحة شخصية",
      "Can't delete a personal workspace",
    ],
    cannot_reparent_personal: [
      "لا يمكن نقل مساحة شخصية",
      "Can't move a personal workspace",
    ],
    parent_not_found: ["القسم الأب غير موجود", "Parent not found"],
    parent_not_container: [
      "يجب أن يكون الأب قسماً (حاوية)",
      "Parent must be a department",
    ],
    parent_cross_org: [
      "لا يمكن النقل عبر مؤسسات مختلفة",
      "Can't move across organizations",
    ],
    parent_cannot_be_personal: [
      "لا يمكن أن يكون الأب مساحة شخصية",
      "Parent can't be a personal workspace",
    ],
    cannot_parent_to_self: [
      "لا يمكن جعل الفريق أباً لنفسه",
      "A team can't be its own parent",
    ],
    would_create_cycle: [
      "هذا النقل ينشئ حلقة في الهيكل",
      "That move would create a cycle",
    ],
    last_owner: [
      "لا يمكن إزالة/تخفيض المالك الأخير",
      "Can't remove/demote the last owner",
    ],
    user_not_in_org: ["المستخدم ليس في المؤسسة", "User not in this org"],
    internal: ["حدث خطأ غير متوقع", "An unexpected error occurred"],
  };
  const hit = code ? map[code] : undefined;
  if (!hit) return ar ? "تعذّر تنفيذ العملية" : "Couldn't complete the action";
  return ar ? hit[0] : hit[1];
}

export default function AdminTeams() {
  const { user, isAuthenticated, loading } = useAuth();
  const { locale } = useI18n();
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  // ROUTE GUARD (Model-2 UI boundary). Org-admin pages are reachable only
  // when the caller can MANAGE a real organization. A solo personal user
  // (owner of an auto-created org shell) is NOT permitted here even by
  // direct URL — the server 403s `admin.*` for them too (source of truth),
  // and this provides the safe redirect instead of a raw error screen.
  const orgQ = trpc.org.me.useQuery(undefined, { enabled: isAuthenticated });
  const canManageOrg = !!(orgQ.data as any)?.canManageOrg;
  const treeQ = trpc.admin.teamsTree.useQuery(undefined, {
    enabled: isAuthenticated && canManageOrg,
  });
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIsContainer, setNewIsContainer] = useState(false);
  const createMut = trpc.admin.create.useMutation();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground p-8 text-center">
        <a href={getLoginUrl()} className="underline">
          {locale === "ar" ? "تسجيل الدخول" : "Sign in"}
        </a>
      </div>
    );
  }

  // Not allowed to manage an organization → safe explanation + redirect.
  // (Wait for org.me to resolve so we don't flash this for real admins.)
  if (orgQ.isSuccess && !canManageOrg) {
    return (
      <PageChrome title={locale === "ar" ? "الإدارة" : "Administration"} backHref="/more">
        <div className="grid place-items-center p-10 text-center text-sm text-muted-foreground gap-3">
          <p>
            {locale === "ar"
              ? "لا توجد مؤسسة لإدارتها بهذا الحساب. مساحتك الشخصية خاصة بك ولا تُدار كشركة."
              : "There's no organization to administer with this account. Your Personal workspace is private and isn't managed like a company."}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate("/")}>
            {locale === "ar" ? "العودة للرئيسية" : "Back to home"}
          </Button>
        </div>
      </PageChrome>
    );
  }

  const tree = treeQ.data;
  const roots = (tree?.roots ?? []) as TreeNode[];
  const flat = tree?.flat ?? [];
  const containers = flat.filter(t => t.isContainer);

  function toggle(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function doCreate() {
    const name = newName.trim();
    if (!name) return;
    try {
      await createMut.mutateAsync({
        name,
        isContainer: newIsContainer,
        parentTeamId: null,
      });
      utils.admin.teamsTree.invalidate();
      setNewName("");
      setNewIsContainer(false);
      setCreateOpen(false);
      toast.success(locale === "ar" ? "تمّ الإنشاء" : "Created");
    } catch (e: any) {
      toast.error(errLabel(e?.message, locale));
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Same chrome as the tabbed screens; bottom tab bar via tabbed(). */}
      <PageChrome
        backHref="/more"
        backLabel={locale === "ar" ? "العودة" : "Back"}
      >
        <div className="mb-6">
          <h1 className="font-display text-2xl sm:text-3xl mb-1">
            {locale === "ar" ? "الإدارة" : "Administration"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {locale === "ar"
              ? "كل إجراء هنا يُسجَّل في سجلّ التدقيق."
              : "Every action here is recorded in the audit log."}
          </p>
        </div>

        {/* 3-tier model explainer — makes the hierarchy legible at a
            glance so "system vs organization" stops being confusing. */}
        <div className="rounded-xl border bg-muted/30 p-4 mb-6 text-sm">
          <div className="font-medium mb-2">
            {locale === "ar" ? "كيف تعمل الصلاحيات" : "How access works"}
          </div>
          <ol className="space-y-1.5 text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">
                {locale === "ar"
                  ? "١) مشرف المنصّة"
                  : "1) Platform admin"}
              </span>:{" "}
              {locale === "ar"
                ? "أنت. صلاحية كاملة على كل المؤسسات."
                : "you. Full control across every organization."}
            </li>
            <li>
              <span className="font-medium text-foreground">
                {locale === "ar"
                  ? "٢) مشرف المؤسسة"
                  : "2) Organization admin"}
              </span>:{" "}
              {locale === "ar"
                ? "يدير مؤسسة واحدة: الخطّة، المقاعد، المشرفون، الدعوات، والأقسام."
                : "manages one organization: plan, seats, admins, invites, and departments."}
            </li>
            <li>
              <span className="font-medium text-foreground">
                {locale === "ar"
                  ? "٣) الأقسام والفرق"
                  : "3) Departments & teams"}
              </span>:{" "}
              {locale === "ar"
                ? "القسم حاوية تضمّ فرقاً. الأعضاء يُضافون إلى الفرق بأدوار (مالك/مدير/قائد/عضو)."
                : "a department is a container that holds teams. People are added to teams with a role (owner / manager / lead / member)."}
            </li>
          </ol>
        </div>

        {/* ── SECTION 1: Organization (Tier 2) ───────────────────── */}
        <div className="mb-3">
          <h2 className="font-display text-lg sm:text-xl">
            {locale === "ar" ? "المؤسسة" : "Organization"}
          </h2>
          <p className="text-xs text-muted-foreground">
            {locale === "ar"
              ? "إعدادات على مستوى المؤسسة، للمشرفين فقط."
              : "Org-wide settings, admins only."}
          </p>
        </div>
        <OrgSettingsSection />
        <OrgInviteSection />

        {/* ── SECTION 2: Departments & teams (Tier 3) ────────────── */}
        <div className="mt-8 mb-3 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg sm:text-xl">
              {locale === "ar"
                ? "الأقسام والفِرق"
                : "Departments & teams"}
            </h2>
            <p className="text-xs text-muted-foreground">
              {locale === "ar"
                ? "أنشئ الأقسام، أضف الفرق، وعيّن الأعضاء."
                : "Create departments, add teams, assign members."}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setCreateOpen(o => !o)}
            className="bg-coral hover:bg-coral/90 text-white gap-1.5 shrink-0"
          >
            <Plus className="h-4 w-4" />
            {locale === "ar" ? "فريق / قسم جديد" : "New team / department"}
          </Button>
        </div>

        {createOpen && (
          <div className="rounded-xl border bg-card p-4 mb-5">
            <div className="text-sm font-medium mb-2">
              {locale === "ar" ? "إنشاء جديد" : "Create new"}
            </div>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder={
                locale === "ar" ? "الاسم" : "Name (e.g. Engineering)"
              }
              className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-coral mb-2"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={newIsContainer}
                onChange={e => setNewIsContainer(e.target.checked)}
                className="accent-coral"
              />
              {locale === "ar"
                ? "قسم (حاوية تضم فرقاً أخرى)"
                : "Department (a container that holds other teams)"}
            </label>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={doCreate}
                disabled={createMut.isPending || !newName.trim()}
                className="bg-coral hover:bg-coral/90 text-white"
              >
                {createMut.isPending && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {locale === "ar" ? "إنشاء" : "Create"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setCreateOpen(false)}
              >
                {locale === "ar" ? "إلغاء" : "Cancel"}
              </Button>
            </div>
          </div>
        )}

        {treeQ.isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {locale === "ar" ? "جاري التحميل…" : "Loading…"}
          </div>
        )}

        {treeQ.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50/60 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">
                {errLabel(treeQ.error?.message, locale)}
              </div>
            </div>
          </div>
        )}

        {tree && (
          <div className="rounded-2xl border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/40 border-b border-border">
              <FolderTree className="h-4 w-4 text-coral shrink-0" />
              <OrgNameEditor
                name={
                  tree.org?.name ??
                  (locale === "ar" ? "المؤسسة" : "Organization")
                }
                canEdit={!!tree.org}
              />
              <span className="text-[11px] text-muted-foreground ms-auto">
                {flat.length}{" "}
                {locale === "ar"
                  ? "فرق/أقسام"
                  : flat.length === 1
                    ? "team"
                    : "teams"}
              </span>
            </div>

            {roots.length === 0 ? (
              <div className="p-8 text-sm text-muted-foreground text-center">
                {locale === "ar"
                  ? "لا فرق بعد. أنشئ فريقاً أو قسماً."
                  : "No teams yet. Create one above."}
              </div>
            ) : (
              <TreeNodes
                nodes={roots}
                expanded={expanded}
                toggle={toggle}
                containers={containers}
                currentUserId={user?.id ?? 0}
              />
            )}
          </div>
        )}
      </PageChrome>
    </div>
  );
}

function TreeNodes({
  nodes,
  expanded,
  toggle,
  containers,
  currentUserId,
  depth = 0,
}: {
  nodes: TreeNode[];
  expanded: Set<number>;
  toggle: (id: number) => void;
  containers: Array<{ id: number; name: string }>;
  currentUserId: number;
  depth?: number;
}) {
  return (
    <ul>
      {nodes.map(t => {
        const isExpanded = expanded.has(t.id);
        return (
          <li key={t.id} className="border-b border-border/40 last:border-b-0">
            <TeamRow
              team={t}
              isExpanded={isExpanded}
              onToggle={() => toggle(t.id)}
              containers={containers}
              currentUserId={currentUserId}
              depth={depth}
            />
            {isExpanded && t.children.length > 0 && (
              <div className="bg-muted/10">
                <TreeNodes
                  nodes={t.children}
                  expanded={expanded}
                  toggle={toggle}
                  containers={containers}
                  currentUserId={currentUserId}
                  depth={depth + 1}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function TeamRow({
  team,
  isExpanded,
  onToggle,
  containers,
  currentUserId,
  depth,
}: {
  team: TreeNode;
  isExpanded: boolean;
  onToggle: () => void;
  containers: Array<{ id: number; name: string }>;
  currentUserId: number;
  depth: number;
}) {
  const { locale } = useI18n();
  const utils = trpc.useUtils();
  const membersQ = trpc.admin.teamMembers.useQuery(
    { teamId: team.id },
    { enabled: isExpanded, staleTime: 30_000 },
  );
  const orgUsersQ = trpc.admin.orgUsers.useQuery(undefined, {
    enabled: isExpanded,
    staleTime: 60_000,
  });
  const reparentMut = trpc.admin.reparent.useMutation();
  const setContainerMut = trpc.admin.setIsContainer.useMutation();
  const changeRoleMut = trpc.admin.changeMemberRole.useMutation();
  const renameMut = trpc.admin.rename.useMutation();
  const deleteMut = trpc.admin.deleteTeam.useMutation();
  const addMemberMut = trpc.admin.addMember.useMutation();
  const removeMemberMut = trpc.admin.removeMember.useMutation();

  const [moveOpen, setMoveOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(team.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addUserId, setAddUserId] = useState<number | "">("");
  const [addRole, setAddRole] = useState<
    "owner" | "manager" | "lead" | "member"
  >("member");

  function refresh() {
    utils.admin.teamsTree.invalidate();
    utils.admin.teamMembers.invalidate({ teamId: team.id });
  }
  function fail(e: any) {
    toast.error(errLabel(e?.message, locale));
  }

  async function doReparent(newParentTeamId: number | null) {
    try {
      await reparentMut.mutateAsync({ teamId: team.id, newParentTeamId });
      utils.admin.teamsTree.invalidate();
      setMoveOpen(false);
      toast.success(locale === "ar" ? "تمّ النقل" : "Moved");
    } catch (e) {
      fail(e);
    }
  }
  async function doRename() {
    const name = nameDraft.trim();
    if (!name || name === team.name) {
      setEditingName(false);
      return;
    }
    try {
      await renameMut.mutateAsync({ teamId: team.id, name });
      utils.admin.teamsTree.invalidate();
      setEditingName(false);
      toast.success(locale === "ar" ? "تمّ التحديث" : "Renamed");
    } catch (e) {
      fail(e);
    }
  }
  async function doDelete() {
    try {
      await deleteMut.mutateAsync({ teamId: team.id });
      utils.admin.teamsTree.invalidate();
      toast.success(locale === "ar" ? "تمّ الحذف" : "Deleted");
    } catch (e) {
      fail(e);
      setConfirmDelete(false);
    }
  }
  async function doToggleContainer() {
    try {
      await setContainerMut.mutateAsync({
        teamId: team.id,
        isContainer: !team.isContainer,
      });
      utils.admin.teamsTree.invalidate();
      toast.success(locale === "ar" ? "تمّ التحويل" : "Converted");
    } catch (e) {
      fail(e);
    }
  }
  async function doChangeRole(
    userId: number,
    role: "owner" | "manager" | "lead" | "member",
  ) {
    try {
      await changeRoleMut.mutateAsync({ teamId: team.id, userId, role });
      utils.admin.teamMembers.invalidate({ teamId: team.id });
      toast.success(locale === "ar" ? "تمّ تحديث الدور" : "Role updated");
    } catch (e) {
      fail(e);
    }
  }
  async function doAddMember() {
    if (addUserId === "") return;
    try {
      await addMemberMut.mutateAsync({
        teamId: team.id,
        userId: Number(addUserId),
        role: addRole,
      });
      refresh();
      setAddUserId("");
      setAddRole("member");
      toast.success(locale === "ar" ? "تمّت الإضافة" : "Member added");
    } catch (e) {
      fail(e);
    }
  }
  async function doRemoveMember(userId: number) {
    try {
      await removeMemberMut.mutateAsync({ teamId: team.id, userId });
      refresh();
      toast.success(locale === "ar" ? "تمّت الإزالة" : "Member removed");
    } catch (e) {
      fail(e);
    }
  }

  const validParents = containers.filter(c => c.id !== team.id);
  const memberIds = new Set((membersQ.data ?? []).map(m => m.user.id));
  const addableUsers = (orgUsersQ.data ?? []).filter(
    u => !memberIds.has(u.id),
  );

  return (
    <div>
      <div
        className="flex items-center gap-2 px-3 py-2.5 hover:bg-muted/30 transition-colors"
        style={{ paddingInlineStart: `${depth * 22 + 12}px` }}
      >
        <button
          type="button"
          onClick={onToggle}
          className="text-muted-foreground hover:text-foreground shrink-0"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {team.isContainer ? (
          <Building2 className="h-4 w-4 text-coral shrink-0" />
        ) : (
          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") doRename();
                  if (e.key === "Escape") {
                    setNameDraft(team.name);
                    setEditingName(false);
                  }
                }}
                className="text-sm rounded-md border border-coral bg-background px-1.5 py-0.5 outline-none w-full max-w-xs"
              />
              <button
                onClick={doRename}
                className="p-1 text-emerald-600 hover:bg-emerald-500/10 rounded"
                aria-label="Save"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => {
                  setNameDraft(team.name);
                  setEditingName(false);
                }}
                className="p-1 text-muted-foreground hover:bg-muted rounded"
                aria-label="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <>
              <div className="font-medium truncate">{team.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {team.isContainer
                  ? locale === "ar"
                    ? "قسم (حاوية)"
                    : "Department · container"
                  : `${team.memberCount} ${locale === "ar" ? "أعضاء" : team.memberCount === 1 ? "member" : "members"}`}
              </div>
            </>
          )}
        </div>
        {!editingName && (
          <div className="flex items-center gap-1 shrink-0">
            <IconBtn
              onClick={() => {
                setNameDraft(team.name);
                setEditingName(true);
              }}
              title={locale === "ar" ? "إعادة تسمية" : "Rename"}
            >
              <Pencil className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn
              onClick={() => setMoveOpen(o => !o)}
              title={locale === "ar" ? "نقل" : "Move to another parent"}
            >
              <FolderTree className="h-3.5 w-3.5" />
            </IconBtn>
            <IconBtn
              onClick={doToggleContainer}
              disabled={setContainerMut.isPending}
              title={
                team.isContainer
                  ? locale === "ar"
                    ? "تحويل لفريق عادي"
                    : "Convert to leaf team"
                  : locale === "ar"
                    ? "تحويل لقسم/حاوية"
                    : "Convert to department"
              }
            >
              {team.isContainer ? (
                <Users className="h-3.5 w-3.5" />
              ) : (
                <Building2 className="h-3.5 w-3.5" />
              )}
            </IconBtn>
            <IconBtn
              onClick={() => setConfirmDelete(true)}
              title={locale === "ar" ? "حذف" : "Delete"}
              danger
            >
              <Trash2 className="h-3.5 w-3.5" />
            </IconBtn>
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="px-3 py-2 bg-red-50/60 dark:bg-red-950/20 border-y border-red-200 flex items-center gap-2 flex-wrap text-sm">
          <span className="text-red-700 dark:text-red-300 flex-1">
            {locale === "ar"
              ? `حذف "${team.name}"؟ (يجب ألا يحتوي على أعضاء أو فرق فرعية)`
              : `Delete "${team.name}"? (must have no members or child teams)`}
          </span>
          <Button
            size="sm"
            onClick={doDelete}
            disabled={deleteMut.isPending}
            className="bg-red-600 hover:bg-red-700 text-white h-7"
          >
            {locale === "ar" ? "حذف" : "Delete"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmDelete(false)}
            className="h-7"
          >
            {locale === "ar" ? "إلغاء" : "Cancel"}
          </Button>
        </div>
      )}

      {moveOpen && (
        <div className="px-3 py-2 bg-muted/30 border-y border-border/40">
          <div className="text-xs text-muted-foreground mb-1.5">
            {locale === "ar" ? "نقل تحت" : "Move under"}:
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => doReparent(null)}
              disabled={reparentMut.isPending || team.parentTeamId == null}
              className="text-[11px] rounded-full border border-border bg-background px-2.5 py-1 hover:bg-muted disabled:opacity-40"
            >
              {locale === "ar" ? "(الجذر)" : "(root level)"}
            </button>
            {validParents.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => doReparent(p.id)}
                disabled={reparentMut.isPending || team.parentTeamId === p.id}
                className="text-[11px] rounded-full border border-border bg-background px-2.5 py-1 hover:bg-muted disabled:opacity-40"
              >
                {p.name}
              </button>
            ))}
            {validParents.length === 0 && (
              <span className="text-[11px] text-muted-foreground italic">
                {locale === "ar"
                  ? "لا توجد أقسام. أنشئ قسماً أولاً"
                  : "No departments yet. Create a container first"}
              </span>
            )}
            <button
              type="button"
              onClick={() => setMoveOpen(false)}
              className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-1"
            >
              {locale === "ar" ? "إلغاء" : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {isExpanded && (
        <div
          className="px-3 py-3 bg-muted/10 border-t border-border/40"
          style={{ paddingInlineStart: `${depth * 22 + 36}px` }}
        >
          {team.isContainer ? (
            <ContainerComparison teamId={team.id} />
          ) : (
            <>
              {membersQ.isLoading && (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading members…
                </div>
              )}
              {membersQ.data && membersQ.data.length > 0 && (
                <ul className="space-y-1 mb-3">
                  {membersQ.data.map(m => {
                    const isMe = m.user.id === currentUserId;
                    return (
                      <li
                        key={m.user.id}
                        className="flex items-center gap-2 text-sm py-1"
                      >
                        <span className="flex-1 min-w-0 truncate">
                          {m.user.name ?? `User ${m.user.id}`}
                          {isMe && (
                            <span className="ms-1.5 text-[10px] text-coral">
                              ({locale === "ar" ? "أنا" : "you"})
                            </span>
                          )}
                        </span>
                        <select
                          value={m.role}
                          onChange={e =>
                            doChangeRole(
                              m.user.id,
                              e.target.value as
                                | "owner"
                                | "manager"
                                | "lead"
                                | "member",
                            )
                          }
                          disabled={changeRoleMut.isPending}
                          className="text-[11px] rounded-md border border-border bg-background px-1.5 py-0.5"
                        >
                          <option value="owner">owner</option>
                          <option value="manager">manager</option>
                          <option value="lead">lead</option>
                          <option value="member">member</option>
                        </select>
                        <IconBtn
                          onClick={() => doRemoveMember(m.user.id)}
                          title={locale === "ar" ? "إزالة" : "Remove"}
                          danger
                        >
                          <X className="h-3.5 w-3.5" />
                        </IconBtn>
                      </li>
                    );
                  })}
                </ul>
              )}
              {membersQ.data && membersQ.data.length === 0 && (
                <div className="text-xs text-muted-foreground italic mb-3">
                  {locale === "ar" ? "لا أعضاء بعد." : "No members yet."}
                </div>
              )}

              <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-border/40">
                <span className="text-[11px] text-muted-foreground">
                  {locale === "ar" ? "إضافة:" : "Add:"}
                </span>
                <select
                  value={addUserId}
                  onChange={e =>
                    setAddUserId(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="text-[11px] rounded-md border border-border bg-background px-1.5 py-1 max-w-[12rem]"
                >
                  <option value="">
                    {locale === "ar" ? "— اختر مستخدماً —" : "— pick user —"}
                  </option>
                  {addableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? u.email ?? `User ${u.id}`}
                    </option>
                  ))}
                </select>
                <select
                  value={addRole}
                  onChange={e =>
                    setAddRole(
                      e.target.value as
                        | "owner"
                        | "manager"
                        | "lead"
                        | "member",
                    )
                  }
                  className="text-[11px] rounded-md border border-border bg-background px-1.5 py-1"
                >
                  <option value="member">member</option>
                  <option value="lead">lead</option>
                  <option value="manager">manager</option>
                  <option value="owner">owner</option>
                </select>
                <Button
                  size="sm"
                  onClick={doAddMember}
                  disabled={addMemberMut.isPending || addUserId === ""}
                  className="h-7 text-[11px] bg-coral hover:bg-coral/90 text-white"
                >
                  {locale === "ar" ? "إضافة" : "Add"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  title,
  danger,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`p-1.5 rounded-md transition-colors disabled:opacity-40 ${
        danger
          ? "text-muted-foreground hover:text-red-600 hover:bg-red-500/10"
          : "text-muted-foreground hover:text-coral hover:bg-coral/10"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Inline editor for the organization name (the SaaS tenant
 * identity). Pencil → edit field → Enter/✓ saves via
 * admin.renameOrg. Mirrors the team-row rename UX so the
 * interaction is consistent across the tree.
 */
function OrgNameEditor({
  name,
  canEdit,
}: {
  name: string;
  canEdit: boolean;
}) {
  const { locale } = useI18n();
  const utils = trpc.useUtils();
  const renameOrgMut = trpc.admin.renameOrg.useMutation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  async function save() {
    const next = draft.trim();
    if (!next || next === name) {
      setEditing(false);
      return;
    }
    try {
      await renameOrgMut.mutateAsync({ name: next });
      utils.admin.teamsTree.invalidate();
      setEditing(false);
      toast.success(locale === "ar" ? "تمّ تحديث اسم المؤسسة" : "Organization renamed");
    } catch (e: any) {
      toast.error(errLabel(e?.message, locale));
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setDraft(name);
              setEditing(false);
            }
          }}
          className="font-display text-base rounded-md border border-coral bg-background px-2 py-0.5 outline-none w-full max-w-sm"
        />
        <button
          onClick={save}
          disabled={renameOrgMut.isPending}
          className="p-1 text-emerald-600 hover:bg-emerald-500/10 rounded disabled:opacity-50"
          aria-label="Save"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            setDraft(name);
            setEditing(false);
          }}
          className="p-1 text-muted-foreground hover:bg-muted rounded"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <span className="font-display text-base inline-flex items-center gap-1.5 min-w-0">
      <span className="truncate">{name}</span>
      {canEdit && (
        <button
          type="button"
          onClick={() => {
            setDraft(name);
            setEditing(true);
          }}
          title={locale === "ar" ? "إعادة تسمية المؤسسة" : "Rename organization"}
          aria-label={locale === "ar" ? "إعادة تسمية المؤسسة" : "Rename organization"}
          className="p-1 rounded-md text-muted-foreground hover:text-coral hover:bg-coral/10 transition-colors shrink-0"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

/**
 * Org-level invite management. Create a link (optionally emailed)
 * that adds the accepter to the ORGANIZATION (not a team — team
 * placement is a separate admin action). Lists pending invites
 * with copy + revoke.
 */
function OrgInviteSection() {
  const { locale } = useI18n();
  const utils = trpc.useUtils();
  const invitesQ = trpc.admin.orgInvites.useQuery();
  const createMut = trpc.admin.createOrgInvite.useMutation();
  const revokeMut = trpc.admin.revokeOrgInvite.useMutation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [lastLink, setLastLink] = useState<string | null>(null);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  async function create() {
    try {
      const r = await createMut.mutateAsync({
        role,
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      const url = `${origin}/org-invite/${r.token}`;
      setLastLink(url);
      setEmail("");
      utils.admin.orgInvites.invalidate();
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(url).catch(() => {});
      }
      toast.success(
        locale === "ar"
          ? "تمّ إنشاء الدعوة (نُسخ الرابط)"
          : "Invite created (link copied)",
      );
    } catch (e: any) {
      toast.error(errLabel(e?.message, locale));
    }
  }

  async function revoke(id: number) {
    try {
      await revokeMut.mutateAsync({ inviteId: id });
      utils.admin.orgInvites.invalidate();
      toast.success(locale === "ar" ? "أُلغيت الدعوة" : "Invite revoked");
    } catch (e: any) {
      toast.error(errLabel(e?.message, locale));
    }
  }

  const invites = invitesQ.data ?? [];
  const pending = invites.filter(
    i => !i.revokedAt && new Date(i.expiresAt).getTime() > Date.now(),
  );

  return (
    <div className="rounded-2xl border bg-card mb-5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-start"
      >
        <Plus className="h-4 w-4 text-coral shrink-0" />
        <span className="font-medium text-sm flex-1">
          {locale === "ar"
            ? "دعوة إلى المؤسسة"
            : "Invite people to the organization"}
        </span>
        {pending.length > 0 && (
          <span className="text-[11px] text-muted-foreground">
            {pending.length}{" "}
            {locale === "ar" ? "قيد الانتظار" : "pending"}
          </span>
        )}
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-border/40 pt-3">
          <p className="text-xs text-muted-foreground mb-3">
            {locale === "ar"
              ? "هذه دعوة على مستوى المؤسسة بالكامل (تختلف عن دعوة الفريق من لوحة التحكم). يُضاف المدعو إلى المؤسسة فقط، ثم تُسند الفرق لاحقاً من الشجرة أدناه."
              : "This is an organization-wide invite (different from the per-team invite on the dashboard). The invitee joins the organization only; assign them to teams afterwards from the tree below."}
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={
                locale === "ar"
                  ? "البريد (اختياري، للإرسال)"
                  : "Email (optional, to send the link)"
              }
              className="flex-1 min-w-[200px] rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-coral"
            />
            <select
              value={role}
              onChange={e =>
                setRole(e.target.value as "admin" | "member")
              }
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            >
              <option value="member">
                {locale === "ar" ? "عضو" : "member"}
              </option>
              <option value="admin">
                {locale === "ar" ? "مدير" : "admin"}
              </option>
            </select>
            <Button
              size="sm"
              onClick={create}
              disabled={createMut.isPending}
              className="bg-coral hover:bg-coral/90 text-white"
            >
              {createMut.isPending && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              {locale === "ar" ? "إنشاء دعوة" : "Create invite"}
            </Button>
          </div>

          {lastLink && (
            <div className="rounded-md bg-muted/40 border border-border px-2.5 py-2 mb-3 flex items-center gap-2">
              <code className="text-[11px] flex-1 truncate">{lastLink}</code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(lastLink);
                  toast.success(locale === "ar" ? "نُسخ" : "Copied");
                }}
                className="text-[11px] text-coral hover:underline shrink-0"
              >
                {locale === "ar" ? "نسخ" : "Copy"}
              </button>
            </div>
          )}

          {pending.length > 0 && (
            <ul className="space-y-1">
              {pending.map(i => (
                <li
                  key={i.id}
                  className="flex items-center gap-2 text-sm py-1 border-t border-border/30 first:border-t-0"
                >
                  <span className="flex-1 min-w-0 truncate">
                    {i.email ?? (locale === "ar" ? "رابط عام" : "link only")}
                    <span className="ms-2 text-[10px] uppercase text-muted-foreground">
                      {i.role}
                    </span>
                  </span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {locale === "ar" ? "ينتهي" : "expires"}{" "}
                    {new Date(i.expiresAt).toLocaleDateString()}
                  </span>
                  <button
                    type="button"
                    onClick={() => revoke(i.id)}
                    disabled={revokeMut.isPending}
                    className="text-[11px] text-muted-foreground hover:text-red-600 shrink-0"
                  >
                    {locale === "ar" ? "إلغاء" : "Revoke"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Phase 7 — cross-team comparison shown when a department
 * (container) is expanded in the admin tree. Side-by-side weekly
 * stats per child team: members, signals, priority completion.
 * Pure read; the current ISO week is used.
 */
function ContainerComparison({ teamId }: { teamId: number }) {
  const { locale } = useI18n();
  // Current week start (Sunday, UTC) — matches the app convention.
  const weekStartKey = (() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - d.getUTCDay());
    return d.toISOString().slice(0, 10);
  })();
  const cmpQ = trpc.team.crossTeamComparison.useQuery(
    { teamId, weekStartKey },
    { staleTime: 30_000 },
  );

  if (cmpQ.isLoading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin" />
        {locale === "ar" ? "جاري التحميل…" : "Loading comparison…"}
      </div>
    );
  }
  // §4 pilot lock — per-child-team completion % is a per-person tracker
  // for small teams; parked until the Phase-4 aggregate redesign.
  if (cmpQ.isError) {
    return (
      <div className="text-xs text-muted-foreground italic" data-testid="cmp-pilot-lock">
        {locale === "ar"
          ? "مقارنة الفرق معطّلة أثناء التجربة."
          : "Team comparison is disabled during the pilot."}
      </div>
    );
  }
  const children = cmpQ.data?.children ?? [];
  if (children.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        {locale === "ar"
          ? "لا فرق فرعية بعد. أضف فرقاً تحت هذا القسم لمقارنتها."
          : "No child teams yet. Add teams under this department to compare them."}
      </div>
    );
  }
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-2">
        {locale === "ar"
          ? `مقارنة الفرق · أسبوع ${weekStartKey}`
          : `Team comparison · week of ${weekStartKey}`}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-[11px] text-muted-foreground text-start">
              <th className="text-start font-medium py-1 pe-3">
                {locale === "ar" ? "الفريق" : "Team"}
              </th>
              <th className="text-start font-medium py-1 pe-3">
                {locale === "ar" ? "أعضاء" : "Members"}
              </th>
              <th className="text-start font-medium py-1 pe-3">
                {locale === "ar" ? "إشارات" : "Signals"}
              </th>
              <th className="text-start font-medium py-1 pe-3">
                {locale === "ar" ? "الأولويّات" : "Priorities"}
              </th>
              <th className="text-start font-medium py-1">
                {locale === "ar" ? "الإنجاز" : "Completion"}
              </th>
            </tr>
          </thead>
          <tbody>
            {children.map(c => (
              <tr key={c.teamId} className="border-t border-border/40">
                <td className="py-1.5 pe-3 font-medium">
                  {c.name}
                  {c.isContainer && (
                    <span className="ms-1 text-[10px] text-muted-foreground">
                      ({locale === "ar" ? "قسم" : "dept"})
                    </span>
                  )}
                </td>
                <td className="py-1.5 pe-3 tabular-nums">{c.memberCount}</td>
                <td className="py-1.5 pe-3 tabular-nums">{c.signalCount}</td>
                <td className="py-1.5 pe-3 tabular-nums">
                  {c.prioritiesDone}/{c.prioritiesTotal}
                </td>
                <td className="py-1.5">
                  {c.completionPct == null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={
                        c.completionPct >= 70
                          ? "text-emerald-600"
                          : c.completionPct >= 40
                            ? "text-amber-600"
                            : "text-red-600"
                      }
                    >
                      {c.completionPct}%
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Phase 9 — organization settings + org-admin management + the
 * SaaS plan/seat boundary. Collapsible. Admin only (server
 * enforces). Shows live seat usage vs cap.
 */
function OrgSettingsSection() {
  const { locale } = useI18n();
  const utils = trpc.useUtils();
  const settingsQ = trpc.admin.orgSettings.useQuery();
  const adminsQ = trpc.admin.orgAdmins.useQuery();
  const orgUsersQ = trpc.admin.orgUsers.useQuery();
  const setMut = trpc.admin.setOrgSettings.useMutation();
  const addAdminMut = trpc.admin.addOrgAdmin.useMutation();
  const removeAdminMut = trpc.admin.removeOrgAdmin.useMutation();
  const [open, setOpen] = useState(false);
  const s = settingsQ.data;
  const [plan, setPlan] = useState<"free" | "pro" | "enterprise">("free");
  const [seatLimit, setSeatLimit] = useState<string>("");
  const [retention, setRetention] = useState<string>("");
  const [addAdminId, setAddAdminId] = useState<number | "">("");

  // Hydrate once settings land.
  if (s && plan !== s.plan && setMut.isIdle && !setMut.isPending) {
    // lightweight one-time sync without an effect import
  }

  function fail(e: any) {
    toast.error(errLabel(e?.message, locale));
  }

  async function save() {
    try {
      await setMut.mutateAsync({
        plan,
        seatLimit: seatLimit.trim() === "" ? null : Number(seatLimit),
        retentionDays: retention.trim() === "" ? null : Number(retention),
      });
      utils.admin.orgSettings.invalidate();
      toast.success(locale === "ar" ? "حُفظت الإعدادات" : "Settings saved");
    } catch (e) {
      fail(e);
    }
  }

  async function addAdmin() {
    if (addAdminId === "") return;
    try {
      await addAdminMut.mutateAsync({ userId: Number(addAdminId) });
      utils.admin.orgAdmins.invalidate();
      setAddAdminId("");
      toast.success(locale === "ar" ? "أُسند كمدير" : "Org admin granted");
    } catch (e) {
      fail(e);
    }
  }
  async function removeAdmin(userId: number) {
    try {
      await removeAdminMut.mutateAsync({ userId });
      utils.admin.orgAdmins.invalidate();
      toast.success(locale === "ar" ? "أُزيلت الصلاحية" : "Admin revoked");
    } catch (e) {
      fail(e);
    }
  }

  const admins = adminsQ.data ?? [];
  const adminIds = new Set(admins.map(a => a.id));
  const nonAdminUsers = (orgUsersQ.data ?? []).filter(
    u => !adminIds.has(u.id),
  );
  const seatPct =
    s && s.seatLimit ? Math.round((s.seatUsed / s.seatLimit) * 100) : null;

  return (
    <div className="rounded-2xl border bg-card mb-5">
      <button
        type="button"
        onClick={() => {
          if (!open && s) {
            setPlan(s.plan as any);
            setSeatLimit(s.seatLimit == null ? "" : String(s.seatLimit));
            setRetention(
              s.retentionDays == null ? "" : String(s.retentionDays),
            );
          }
          setOpen(o => !o);
        }}
        className="w-full flex items-center gap-2 px-4 py-3 text-start"
      >
        <Building2 className="h-4 w-4 text-coral shrink-0" />
        <span className="font-medium text-sm flex-1">
          {locale === "ar"
            ? "إعدادات المؤسسة والخطة"
            : "Organization settings & plan"}
        </span>
        {s && (
          <span className="text-[11px] text-muted-foreground">
            {s.plan} ·{" "}
            {s.seatLimit
              ? `${s.seatUsed}/${s.seatLimit} ${locale === "ar" ? "مقعد" : "seats"}`
              : `${s.seatUsed} ${locale === "ar" ? "أعضاء" : "members"}`}
          </span>
        )}
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {open && s && (
        <div className="px-4 pb-4 border-t border-border/40 pt-3 space-y-4">
          {/* Plan + seats + retention */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-xs">
              <span className="block text-muted-foreground mb-1">
                {locale === "ar" ? "الخطة" : "Plan"}
              </span>
              <select
                value={plan}
                onChange={e => setPlan(e.target.value as any)}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="free">free</option>
                <option value="pro">pro</option>
                <option value="enterprise">enterprise</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="block text-muted-foreground mb-1">
                {locale === "ar" ? "حدّ المقاعد" : "Seat limit"}
              </span>
              <input
                type="number"
                value={seatLimit}
                onChange={e => setSeatLimit(e.target.value)}
                placeholder={locale === "ar" ? "غير محدود" : "unlimited"}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="block text-muted-foreground mb-1">
                {locale === "ar"
                  ? "احتفاظ البيانات (يوم)"
                  : "Retention (days)"}
              </span>
              <input
                type="number"
                value={retention}
                onChange={e => setRetention(e.target.value)}
                placeholder={locale === "ar" ? "افتراضي" : "server default"}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          {seatPct != null && (
            <div className="text-[11px] text-muted-foreground">
              {locale === "ar" ? "استخدام المقاعد" : "Seat usage"}:{" "}
              <span
                className={
                  seatPct >= 100
                    ? "text-red-600 font-medium"
                    : seatPct >= 80
                      ? "text-amber-600"
                      : ""
                }
              >
                {s.seatUsed}/{s.seatLimit} ({seatPct}%)
              </span>
              {seatPct >= 100 && (
                <span className="ms-2 text-red-600">
                  {locale === "ar"
                    ? "الدعوات الجديدة مرفوضة."
                    : "New joins are blocked."}
                </span>
              )}
            </div>
          )}

          <div>
            <Button
              size="sm"
              onClick={save}
              disabled={setMut.isPending}
              className="bg-coral hover:bg-coral/90 text-white"
            >
              {setMut.isPending && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              {locale === "ar" ? "حفظ" : "Save settings"}
            </Button>
            <span className="text-[10px] text-muted-foreground ms-3">
              {locale === "ar"
                ? "billingStatus يُدار عبر مزود الدفع لاحقاً."
                : "billingStatus is managed by the payment provider later."}
            </span>
          </div>

          {/* Org admins */}
          <div className="border-t border-border/40 pt-3">
            <div className="text-xs font-medium mb-2">
              {locale === "ar" ? "مدراء المؤسسة" : "Organization admins"}
            </div>
            <ul className="space-y-1 mb-2">
              {admins.map(a => (
                <li
                  key={a.id}
                  className="flex items-center gap-2 text-sm py-1"
                >
                  <span className="flex-1 min-w-0 truncate">
                    {a.name ?? a.email ?? `User ${a.id}`}
                    {a.isOwner && (
                      <span className="ms-1.5 text-[10px] uppercase text-coral">
                        {locale === "ar" ? "مالك" : "owner"}
                      </span>
                    )}
                  </span>
                  {!a.isOwner && (
                    <button
                      type="button"
                      onClick={() => removeAdmin(a.id)}
                      disabled={removeAdminMut.isPending}
                      className="text-[11px] text-muted-foreground hover:text-red-600"
                    >
                      {locale === "ar" ? "إزالة" : "Revoke"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-1.5 flex-wrap">
              <select
                value={addAdminId}
                onChange={e =>
                  setAddAdminId(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="text-[11px] rounded-md border border-border bg-background px-1.5 py-1 max-w-[14rem]"
              >
                <option value="">
                  {locale === "ar" ? "— اختر مستخدماً —" : "— pick user —"}
                </option>
                {nonAdminUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name ?? u.email ?? `User ${u.id}`}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={addAdmin}
                disabled={addAdminMut.isPending || addAdminId === ""}
                className="h-7 text-[11px] bg-coral hover:bg-coral/90 text-white"
              >
                {locale === "ar" ? "إسناد كمدير" : "Grant admin"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
